<template>
  <div v-if="localState">
    <poppins-popup v-model="showUnsavedChangesPopup" popup-header="Unsaved changes">
      <p class="text-body mb-16">If you leave your changes will not be saved</p>
      <poppins-button value="Discard" size="medium" variant="primary" class="w-full" @click="confirmDiscard" />
    </poppins-popup>
    <div class="w-full mx-auto mb-24 max-w-688 px-16">
      <poppins-breadcrumb header="Back" is-button class="mb-24 !mt-0" @click="handleLeave" />
      <h1 class="text-h2 mb-8">Certifications and Training</h1>
      <p class="text-body-3 mb-24">Add details so families can verify with the issuing organization.</p>
      <poppins-card class="w-full px-16 py-24 md:px-24" custom-classes="!rounded-2xl">
        <ul class="flex flex-col gap-16">
          <li v-for="cert in certificationItems" :key="cert.value">
            <certification-form-card
              :value="cert.value"
              :title="cert.title"
              :icon="cert.icon"
              :is-added="cert.isAdded"
              :is-expanded="cert.isExpanded"
              :details="cert.details"
              :organization-options="organizationOptions"
              :month-options="monthOptions"
              :issue-year-options="issueYearOptions"
              :expiration-year-options="expirationYearOptions"
              :show-required-error="showRequiredError"
              @toggle="toggleCertification(cert.value)"
              @update:details="updateDetails(cert.value, $event)"
            />
          </li>
        </ul>
      </poppins-card>
      <div class="flex justify-end gap-8 mt-24">
        <poppins-button variant="outlined" value="Cancel" size="medium" type="button" @click="handleLeave" />
        <poppins-button :loading="saving" variant="primary" value="Save" size="medium" type="button" @click="handleSave" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { format, isValid, parseISO } from 'date-fns';
import PoppinsCard from '@/components/card/Card.vue';
import PoppinsBreadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import PoppinsButton from '@/components/button/Button.vue';
import PoppinsPopup from '@/components/popup/Popup.vue';
import CertificationFormCard from './CertificationFormCard.vue';
import CPRBadge from '@/graphics/badges/CPRBadge.vue';
import FirstAidBadge from '@/graphics/badges/FirstAidBadge.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import { markComponentRaw } from '@/helpers/markComponentRaw';
import {
  CERTIFICATIONS_OPTIONS,
  CERTIFICATION_ORGANIZATION_OPTIONS,
  MONTH_OPTIONS,
  certificationIssueYearOptions,
  certificationExpirationYearOptions,
  emptyCertificationDetails,
  hasCertificationDetails,
  isCertificationExpirationInThePast,
  type CertificationDetails,
} from '../../__types__/CareTypeFormOptions';

const CERTIFICATION_VALUES = new Set(CERTIFICATIONS_OPTIONS.map((option) => option.value));

const QUALIFICATION_TO_CERT_TYPE: Record<string, string> = {
  CPR_CERTIFIED: 'CPR',
  FIRST_AID_TRAINING: 'FIRST_AID',
};

const CERT_TYPE_TO_QUALIFICATION: Record<string, string> = {
  CPR: 'CPR_CERTIFIED',
  FIRST_AID: 'FIRST_AID_TRAINING',
};

const qualificationForCertType = (type?: unknown): string => {
  if (type === 'CPR' || type === 'FIRST_AID') return CERT_TYPE_TO_QUALIFICATION[type];
  return '';
};

const CERTIFICATION_CARDS = [
  {
    value: 'CPR_CERTIFIED',
    title: 'CPR certification',
    icon: markComponentRaw(CPRBadge),
  },
  {
    value: 'FIRST_AID_TRAINING',
    title: 'First aid certification',
    icon: markComponentRaw(FirstAidBadge),
  },
];

interface ProviderCertification {
  type?: string;
  issuingOrganization?: string | null;
  issuedOn?: Date | string | null;
  expiresOn?: Date | string | null;
}

const emptyDetailsByCertification = () =>
  CERTIFICATION_CARDS.reduce(
    (details, cert) => {
      details[cert.value] = emptyCertificationDetails();
      return details;
    },
    {} as Record<string, CertificationDetails>
  );

const parseCertificationDate = (value?: Date | string | null) => {
  if (!value) return { month: '', year: '' };

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})/);
    if (match) return { month: match[2], year: match[1] };
  }

  const date = typeof value === 'string' ? parseISO(value) : new Date(value);
  if (!isValid(date)) return { month: '', year: '' };
  return { month: format(date, 'MM'), year: format(date, 'yyyy') };
};

const toCertificationDate = (month?: string, year?: string) => {
  if (!month || !year) return null;
  const paddedMonth = String(month).padStart(2, '0');
  if (!/^\d{2}$/.test(paddedMonth) || !/^\d{4}$/.test(String(year))) return null;
  return `${year}-${paddedMonth}-01`;
};

const detailsFromCertification = (cert?: ProviderCertification): CertificationDetails => {
  const issued = parseCertificationDate(cert?.issuedOn);
  const expires = parseCertificationDate(cert?.expiresOn);
  const organizationValue = cert?.issuingOrganization || '';
  const organizationOption = CERTIFICATION_ORGANIZATION_OPTIONS.find(
    (option) => option.value === organizationValue || option.display === organizationValue
  );
  const hasAnyDetail = Boolean(organizationValue || cert?.issuedOn || cert?.expiresOn);

  return {
    organization: organizationOption?.value || organizationValue,
    issueMonth: issued.month,
    issueYear: issued.year,
    expirationMonth: expires.month,
    expirationYear: expires.year,
    doesNotExpire: hasAnyDetail && !cert?.expiresOn,
    remove: false,
  };
};

export default {
  name: 'CertificationsForm',
  components: {
    PoppinsCard,
    PoppinsBreadcrumb,
    PoppinsButton,
    PoppinsPopup,
    CertificationFormCard,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
      expandedIds: [] as string[],
      showUnsavedChangesPopup: false,
      saving: false,
      showRequiredError: false,
    };
  },
  computed: {
    organizationOptions: () => CERTIFICATION_ORGANIZATION_OPTIONS,
    monthOptions: () => MONTH_OPTIONS,
    issueYearOptions: () => certificationIssueYearOptions(),
    expirationYearOptions: () => certificationExpirationYearOptions(),
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
    hasMissingRequiredFields(): boolean {
      if (!this.localState) return false;
      return this.expandedIds.some((value) => {
        const details = this.localState.details?.[value];
        if (!details || details.remove) return false;
        if (!details.organization) return true;
        if (details.doesNotExpire) return false;
        if (!details.expirationMonth || !details.expirationYear) return true;
        return isCertificationExpirationInThePast(details.expirationMonth, details.expirationYear);
      });
    },
    certificationItems() {
      const selected = new Set(this.localState?.certifications || []);
      return CERTIFICATION_CARDS.map((cert) => ({
        ...cert,
        isAdded: selected.has(cert.value),
        isExpanded: this.expandedIds.includes(cert.value),
        details: this.localState?.details?.[cert.value] || emptyCertificationDetails(),
      }));
    },
  },
  watch: {
    $route() {
      this.localState = null;
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    uniqueValues(values: string[] = []): string[] {
      return Array.from(new Set(values.filter(Boolean)));
    },
    certificationRecords(state?: { certifications?: ProviderCertification[] } | null): ProviderCertification[] {
      return Array.isArray(state?.certifications) ? state.certifications.filter(Boolean) : [];
    },
    detailsFromState(certifications: ProviderCertification[] = []): Record<string, CertificationDetails> {
      const details = emptyDetailsByCertification();
      this.certificationRecords({ certifications }).forEach((cert) => {
        const value = qualificationForCertType(cert.type);
        if (!value) return;
        details[value] = detailsFromCertification(cert);
      });
      return details;
    },
    initializeFormState() {
      const state = this.formState as { qualifications?: string[]; certifications?: ProviderCertification[] } | null;
      if (!state) return;

      if (this.localState) {
        this.hydrateMissingDetails(this.certificationRecords(state));
        return;
      }

      this.localState = {
        certifications: this.uniqueValues(this.certificationRecords(state).map((cert) => qualificationForCertType(cert.type))),
        details: this.detailsFromState(state.certifications),
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
      this.expandedIds = [];
    },
    hydrateMissingDetails(certifications: ProviderCertification[] = []) {
      if (!this.localState || this.hasUnsavedChanges) return;

      certifications.forEach((cert) => {
        const value = qualificationForCertType(cert.type);
        if (!value || hasCertificationDetails(this.localState.details[value])) return;

        const incoming = detailsFromCertification(cert);
        if (!hasCertificationDetails(incoming)) return;

        this.localState.details = {
          ...this.localState.details,
          [value]: incoming,
        };
        if (!this.localState.certifications.includes(value)) {
          this.localState.certifications = this.uniqueValues([...this.localState.certifications, value]);
        }
      });
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    updateDetails(value: string, details: CertificationDetails) {
      if (!this.localState) return;
      this.localState.details = {
        ...this.localState.details,
        [value]: details,
      };
      if (!this.hasMissingRequiredFields) this.showRequiredError = false;
    },
    toggleCertification(value: string) {
      if (this.expandedIds.includes(value)) {
        this.expandedIds = this.expandedIds.filter((id) => id !== value);
        return;
      }
      this.expandedIds = [...this.expandedIds, value];
    },
    handleLeave() {
      if (this.hasUnsavedChanges) {
        this.showUnsavedChangesPopup = true;
        return;
      }
      this.cancelEdit();
    },
    confirmDiscard() {
      this.showUnsavedChangesPopup = false;
      this.cancelEdit();
    },
    certificationInput(value: string, details: CertificationDetails) {
      const organizationOption = CERTIFICATION_ORGANIZATION_OPTIONS.find((option) => option.value === details.organization);

      return {
        type: QUALIFICATION_TO_CERT_TYPE[value],
        issuingOrganization: organizationOption?.display || details.organization || null,
        issuedOn: toCertificationDate(details.issueMonth, details.issueYear),
        expiresOn: details.doesNotExpire ? null : toCertificationDate(details.expirationMonth, details.expirationYear),
      };
    },
    async handleSave() {
      if (!this.localState || this.saving) return;
      if (this.hasMissingRequiredFields) {
        this.showRequiredError = true;
        return;
      }

      this.saving = true;
      try {
        const markedForRemoval = new Set(
          Object.entries(this.localState.details as Record<string, CertificationDetails>)
            .filter(([, details]) => details.remove)
            .map(([value]) => value)
        );
        const certifications = this.uniqueValues([
          ...this.localState.certifications.filter((value) => !markedForRemoval.has(value)),
          ...this.expandedIds.filter((value) => !markedForRemoval.has(value)),
        ]);

        const otherQualifications = this.uniqueValues(
          ((this.formState as { qualifications?: string[] })?.qualifications || []).filter(
            (value) => !CERTIFICATION_VALUES.has(value)
          )
        );

        this.localState.certifications = certifications;
        this.localState.details = {
          ...this.localState.details,
          ...Object.fromEntries([...markedForRemoval].map((value) => [value, emptyCertificationDetails()])),
        };
        await this.saveSection({
          qualifications: this.uniqueValues([...otherQualifications, ...certifications]),
          providerCertificationInputs: certifications.map((value) =>
            this.certificationInput(value, this.localState.details[value])
          ),
          removedCertificationTypes: [...markedForRemoval].map((value) => QUALIFICATION_TO_CERT_TYPE[value]).filter(Boolean),
        });
        this.originalState = JSON.parse(JSON.stringify(this.localState));
        this.expandedIds = [];
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
